import { getCartQuantity, ON_INIT_CART } from '@nike/ux-tread-cart-redux';
import { toggleBodyOverflow } from '@nike/ux-tread-dotcom-utils';
import { CART_COUNT_UPDATED_EVENT_NAME, UPDATED_SHOP_DATA_EVENT } from '@nike/custom-event-names';
import { addPageAction } from '@nike/ux-tread-new-relic';
import { CART_STATE_KEY } from '@nike/cart-constants';

import { UPDATE_SHARED_DATA } from '../../state/redux/features/shared/types';
import getHasCartErrorsOrWarnings from '../../state/redux/selectors/get-has-cart-errors-or-warnings';
import observeStore from '../../state/redux/observe-store';
import deriveUrlWithFragment from '../../utils/derive-url-with-fragment';
import dispatchCustomEvent from '../../utils/dispatch-custom-event';
import createCustomEvent from '../../utils/create-custom-event';
import initializeUser from './initialize-user';
import onCartUpdateSideEffects from './on-cart-update-side-effects';
import getWebShellClientLocale from '../../state/redux-replacement/get-webshellclient-locale';

// NOTE - inside this function is also possibly where our interaction with CS user (admin vs unite) will come into play
export default async function initUserAndObserveStore({
  shopInstance,
  onCartUpdateCallback,
  fetchCartCallback,
  errorsToExclude,
  warningsToInclude,
  startTime,
}) {
  try {
    // initialize web shell client identity
    await window?.webShellClient?.identity.initialize({ isClient: true });
    // we are going to set a variable for the default start of cartCount (events, observer also need this)
    let cartCount = 0;
    const { visitorId } = (await window?.webShellClient?.identity.getVisitData()) || {};
    const token = await window?.webShellClient?.identity.getAccessToken();
    const userProfile = await window?.webShellClient?.identity.getUserProfile();
    const isLoggedIn = await window.webShellClient.identity.getIsLoggedIn();
    const userType = userProfile?.userType;

    addPageAction('nike-shop-shell-identity-event', {
      version: window?.NikeShop?.version,
      loadTime: Date.now() - startTime,
      status: 'Initialized',
      userType,
    });

    let attempted = false;
    const cartHref = deriveUrlWithFragment('cart');
    const favoritesHref = deriveUrlWithFragment('favorites');
    const checkoutHref = deriveUrlWithFragment('checkout');
    const locale = getWebShellClientLocale();
    const payload = {
      ...locale,
      cartHref,
      favoritesHref,
      checkoutHref,
    };
    shopInstance.internalStore.dispatch({
      type: UPDATE_SHARED_DATA,
      payload,
    });
    const updatedSharedDataEvent = createCustomEvent(UPDATED_SHOP_DATA_EVENT, {
      bubbles: true,
      detail: { cartHref, favoritesHref, checkoutHref },
    });
    dispatchCustomEvent(updatedSharedDataEvent);
    const langLocale = locale?.langRegion?.toLowerCase().replace('-', '_'); // NOTE This is due to cart-redux and when we clean it up there we can clean it up here
    const localeForCurrency = locale?.hreflang;
    // NOTE this is to fix dotcomNav from our end, will need to go away once we clean up consumer used code
    shopInstance.store.dispatch({
      type: ON_INIT_CART,
      payload: { country: shopInstance.country },
      meta: {
        langLocale,
        localeForCurrency,
        currency: locale?.currency,
        isLoggedIn: isLoggedIn,
      },
    });

    observeStore(shopInstance.store, state => {
      // determine if cart is initialized already
      const nextCartCount = getCartQuantity(shopInstance.store.getState());
      // We need to fetch cart at least ONCE and so if nothing outside has fetched cart we'll do this here.
      // However since we ourselves are invoking fetchCart, when no config is set you might fetch multiple times
      // inside the observer before the config gets set. We are using the `attempted` variable to keep track.
      if (!attempted && (token || visitorId)) {
        attempted = true;
        // fetch and initialize cart if is has not already been done
        fetchCartCallback();
      }

      if (cartCount !== nextCartCount) {
        cartCount = getCartQuantity(shopInstance.store.getState());

        onCartUpdateSideEffects({
          cart: shopInstance.store.getState()[CART_STATE_KEY],
        });

        const cartCountEvent = createCustomEvent(CART_COUNT_UPDATED_EVENT_NAME, {
          bubbles: true,
          detail: { cartCount },
        });
        dispatchCustomEvent(cartCountEvent);
      }

      // check if there are any errors
      const hasCartErrors = getHasCartErrorsOrWarnings(state, {
        errorsToExclude,
        warningsToInclude,
      });

      // toggleBodyOverflow will turn on and off based on if there are errors
      toggleBodyOverflow(hasCartErrors);
    });

    initializeUser({
      onCartUpdate: onCartUpdateCallback,
      fetchCartCallback,
      shopInstance,
    });
  } catch (error) {
    return shopInstance.onErrorShellIdentityCallback();
  }
}
